import { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type ThemeState, token } from '@atlaskit/tokens';

import { createBuiltInColorMap } from '../color-map';
import { useDarkModeColors } from '../use-dark-mode-colors';

/** This is a map of commonly used ADS colors, a well-balanced spread of the color palette
 * There are 3 unique sets of tokens for background, text, and border.
 * Highlight colors, otherwise known as foreground colors, are the same as text colors
 * because text colors are handpicked to complement background colors at the appropriate AA contrast ratio
 * contrast standards should be met between foreground/background as text/background does
 */

const knownBackgroundColors: string[] = [
	token('elevation.surface'),
	token('color.background.accent.blue.subtlest'),
	token('color.background.accent.purple.subtlest'),
	token('color.background.accent.magenta.subtlest'),
	token('color.background.accent.red.subtlest'),
	token('color.background.accent.orange.subtlest'),
	token('color.background.accent.yellow.subtlest'),
	token('color.background.accent.lime.subtlest'),
	token('color.background.accent.green.subtlest'),
	token('color.background.accent.teal.subtlest'),
	token('color.background.accent.gray.subtler'),
	token('color.background.accent.blue.subtler'),
	token('color.background.accent.purple.subtler'),
	token('color.background.accent.magenta.subtler'),
	token('color.background.accent.red.subtler'),
	token('color.background.accent.orange.subtler'),
	token('color.background.accent.yellow.subtler'),
	token('color.background.accent.lime.subtler'),
	token('color.background.accent.green.subtler'),
	token('color.background.accent.teal.subtler'),
	token('color.background.accent.gray.subtle'),
	token('color.background.accent.blue.subtle'),
	token('color.background.accent.purple.subtle'),
	token('color.background.accent.magenta.subtle'),
	token('color.background.accent.red.subtle'),
	token('color.background.accent.orange.subtle'),
	token('color.background.accent.yellow.subtle'),
	token('color.background.accent.lime.subtle'),
	token('color.background.accent.green.subtle'),
	token('color.background.accent.teal.subtle'),
	token('color.background.accent.gray.bolder'),
	token('color.background.accent.blue.bolder'),
	token('color.background.accent.purple.bolder'),
	token('color.background.accent.magenta.bolder'),
	token('color.background.accent.red.bolder'),
	token('color.background.accent.orange.bolder'),
	token('color.background.accent.yellow.bolder'),
	token('color.background.accent.lime.bolder'),
	token('color.background.accent.green.bolder'),
	token('color.background.accent.teal.bolder'),
];
// New row of background colors are not semantically correct,
// and will be replaced by actual background colors from ADS
// when available.
// For now, we use these to solve a customer problem by helping
// them select a correctly theme-responsive token value instead
// of having to resort to custom hex codes to get a dark background
// color. Custom hex color codes don't always work well when used alongside
// tokenized text colors because the ADS color tokens will switch
// to a contrasting value in the opposite theme mode, but the custom
// hex background color will stay the same (we only change custom hex
// code values by darkening them if it is a light value selected in light mode).
const additionalBackgroundColors: string[] = [
	token('color.text.accent.gray.bolder'),
	token('color.text.accent.blue'),
	token('color.text.accent.purple'),
	token('color.text.accent.magenta'),
	token('color.text.accent.red'),
	token('color.text.accent.orange'),
	token('color.text.accent.yellow'),
	token('color.text.accent.lime'),
	token('color.text.accent.green'),
	token('color.text.accent.teal'),
];

const knownTextColors: string[] = [
	token('color.text'),
	token('color.text.accent.blue.bolder'),
	token('color.text.accent.purple.bolder'),
	token('color.text.accent.magenta.bolder'),
	token('color.text.accent.red.bolder'),
	token('color.text.accent.orange.bolder'),
	token('color.text.accent.yellow.bolder'),
	token('color.text.accent.lime.bolder'),
	token('color.text.accent.green.bolder'),
	token('color.text.accent.teal.bolder'),
	token('color.text.subtle'),
	token('color.text.accent.blue'),
	token('color.text.accent.purple'),
	token('color.text.accent.magenta'),
	token('color.text.accent.red'),
	token('color.text.accent.orange'),
	token('color.text.accent.yellow'),
	token('color.text.accent.lime'),
	token('color.text.accent.green'),
	token('color.text.accent.teal'),
	token('color.text.inverse'),
	token('color.background.accent.blue.subtler'),
	token('color.background.accent.purple.subtler'),
	token('color.background.accent.magenta.subtler'),
	token('color.background.accent.red.subtler'),
	token('color.background.accent.orange.subtler'),
	token('color.background.accent.yellow.subtler'),
	token('color.background.accent.lime.subtler'),
	token('color.background.accent.green.subtler'),
	token('color.background.accent.teal.subtler'),
];

const knownBorderColors: string[] = [
	token('color.background.accent.gray.subtler'),
	token('color.border.accent.blue'),
	token('color.border.accent.purple'),
	token('color.border.accent.magenta'),
	token('color.border.accent.red'),
	token('color.border.accent.orange'),
	token('color.border.accent.yellow'),
	token('color.border.accent.lime'),
	token('color.border.accent.green'),
	token('color.border.accent.teal'),
	token('color.border.inverse'),
	token('color.border.accent.gray'),
];

export const knownLabelColors: string[] = [
	token('elevation.surface'), // Default label color
	token('color.background.accent.blue.subtlest'),
	token('color.background.accent.purple.subtlest'),
	token('color.background.accent.magenta.subtlest'),
	token('color.background.accent.red.subtlest'),
	token('color.background.accent.orange.subtlest'),
	token('color.background.accent.yellow.subtlest'),
	token('color.background.accent.lime.subtlest'),
	token('color.background.accent.green.subtlest'),
	token('color.background.accent.teal.subtlest'),
	token('color.background.accent.gray.subtler'),
	token('color.background.accent.blue.subtler'),
	token('color.background.accent.purple.subtler'),
	token('color.background.accent.magenta.subtler'),
	token('color.background.accent.red.subtler'),
	token('color.background.accent.orange.subtler'),
	token('color.background.accent.yellow.subtler'),
	token('color.background.accent.lime.subtler'),
	token('color.background.accent.green.subtler'),
	token('color.background.accent.teal.subtler'),
	token('color.background.accent.gray.subtle'),
	token('color.background.accent.blue.subtle'),
	token('color.background.accent.purple.subtle'),
	token('color.background.accent.magenta.subtle'),
	token('color.background.accent.red.subtle'),
	token('color.background.accent.orange.subtle'),
	token('color.background.accent.yellow.subtle'),
	token('color.background.accent.lime.subtle'),
	token('color.background.accent.green.subtle'),
	token('color.background.accent.teal.subtle'),
	token('color.background.accent.gray.bolder'),
	token('color.background.accent.blue.bolder'),
	token('color.background.accent.purple.bolder'),
	token('color.background.accent.magenta.bolder'),
	token('color.background.accent.red.bolder'),
	token('color.background.accent.orange.bolder'),
	token('color.background.accent.yellow.bolder'),
	token('color.background.accent.lime.bolder'),
	token('color.background.accent.green.bolder'),
	token('color.background.accent.teal.bolder'),
	token('color.text.accent.gray.bolder'),
	token('color.text.accent.blue.bolder'),
	token('color.text.accent.purple.bolder'),
	token('color.text.accent.magenta.bolder'),
	token('color.text.accent.red.bolder'),
	token('color.text.accent.orange.bolder'),
	token('color.text.accent.yellow.bolder'),
	token('color.text.accent.lime.bolder'),
	token('color.text.accent.green.bolder'),
	token('color.text.accent.teal.bolder'),
];

const knownHighlightColors: string[] = knownTextColors;

export const useBuiltInColorMap = (getSavedThemeLocal: () => Partial<ThemeState>) => {
	const { getDarkModeCompatibleBackgroundColor } = useDarkModeColors(getSavedThemeLocal);

	return useMemo(() => {
		const backgroundColorMap = createBuiltInColorMap({
			knownColors: [
				...knownBackgroundColors,
				...(fg('company_hub_add_background_palette_tokens') ? additionalBackgroundColors : []),
			],
			getResponsiveColor: (color: string) => {
				return getDarkModeCompatibleBackgroundColor(color);
			},
		});

		const borderColorMap = createBuiltInColorMap({
			knownColors: knownBorderColors,
		});
		const textColorMap = createBuiltInColorMap({
			knownColors: knownTextColors,
		});
		const highlightColorMap = createBuiltInColorMap({
			knownColors: knownHighlightColors,
		});

		const labelColorMap = createBuiltInColorMap({
			knownColors: knownLabelColors,
		});

		return { backgroundColorMap, borderColorMap, textColorMap, highlightColorMap, labelColorMap };
	}, [getDarkModeCompatibleBackgroundColor]);
};
